import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { RecordNumberHint } from './recordNumberHint';

/**
 * Under a record-number field: the highest number given so far, and the next
 * one OFFERED as a button — like the Tagebuch's times from the Zeiterfassung, a
 * value that fills the field on a click and never writes itself. Where the
 * Kontingente leave no number above the highest, it says so instead.
 *
 * Rendered inside the field's helper text, a `<p>`: everything here is
 * phrasing content (spans and a button), no block element.
 */
export default function RecordNumberHintLine({
    hint,
    current,
    onApply
}: {
    hint: RecordNumberHint;
    /** What the field holds now — the button rests once it is the next. */
    current: string;
    onApply: (number: string) => void;
}) {
    const intl = useIntl();
    const next = hint.next === null ? '' : String(hint.next);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 1
            }}
        >
            <FormattedMessage
                id="recordNumber.hint.highest"
                values={{ number: hint.highest }}
            />
            {next ? (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'recordNumber.hint.apply'
                    })}
                >
                    {/* A disabled button fires no events, so the tooltip needs
                        a wrapper. */}
                    <span>
                        <Button
                            size="small"
                            startIcon={<ArrowUpwardIcon />}
                            disabled={current.trim() === next}
                            onClick={() => onApply(next)}
                        >
                            <FormattedMessage
                                id="recordNumber.hint.take"
                                values={{ number: next }}
                            />
                        </Button>
                    </span>
                </Tooltip>
            ) : (
                <Box
                    component="span"
                    role="alert"
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'warning.main'
                    }}
                >
                    <WarningAmberIcon fontSize="small" />
                    <FormattedMessage id="recordNumber.hint.quotaFull" />
                </Box>
            )}
        </Box>
    );
}
